import {
  createContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { createElement } from 'react';
import type { Role } from '@avgrp/shared';
import { setToken, clearToken, getToken, apiPost } from '@/services/api';
import { connectSocket, disconnectSocket } from '@/services/socket';
import { API_ROUTES } from '@avgrp/shared';

// ─── Types ──────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  role: Role;
  organizationId: string;
  baseId: string;
}

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface AuthActions {
  requestOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

export type AuthContextValue = AuthState & AuthActions;

// ─── Context ────────────────────────────────────────────────

const defaultContext: AuthContextValue = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  requestOtp: async () => {},
  verifyOtp: async () => {},
  logout: async () => {},
};

export const AuthContext = createContext<AuthContextValue>(defaultContext);

// ─── Storage keys ───────────────────────────────────────────

const USER_KEY = 'avgrp_user';

function loadStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (raw) {
      return JSON.parse(raw) as AuthUser;
    }
  } catch {
    localStorage.removeItem(USER_KEY);
  }
  return null;
}

// ─── Provider ───────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    const token = getToken();
    const storedUser = loadStoredUser();

    if (token && storedUser) {
      setUser(storedUser);
      connectSocket();
    }

    setIsLoading(false);
  }, []);

  const requestOtp = useCallback(async (email: string) => {
    await apiPost(API_ROUTES.AUTH.REQUEST_OTP, { email });
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string) => {
    const response = await apiPost<{
      token: string;
      expiresAt: string;
      user: AuthUser;
    }>(API_ROUTES.AUTH.VERIFY_OTP, { email, code });

    const { token, user: authUser } = response;

    setToken(token);
    localStorage.setItem(USER_KEY, JSON.stringify(authUser));
    setUser(authUser);
    connectSocket();
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost(API_ROUTES.AUTH.LOGOUT);
    } catch {
      // Proceed with local cleanup even if server call fails
    }

    clearToken();
    localStorage.removeItem(USER_KEY);
    setUser(null);
    disconnectSocket();
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    requestOtp,
    verifyOtp,
    logout,
  };

  return createElement(AuthContext.Provider, { value }, children);
}
